/**
 * Bezugsteuer in der Schweiz (Art. 45 MWSTG) bzw. in Liechtenstein (Art. 45 MWSTG FL, eigenes Gesetz, Steuerverwaltung Liechtenstein), nur als Hinweis in der Rechnungsprüfung, keine Berechnung: Bezieht eine Agentur
 * mit Sitz in der Schweiz oder in Liechtenstein Leistungen eines Creators mit Sitz im Ausland (Ort der Leistung beim
 * Empfänger, Art. 8 Abs. 1 MWSTG), enthält dessen Rechnung keine Schweizer MWST. Die Agentur rechnet die Steuer auf dem Bezug
 * selbst in ihrer MWST-Abrechnung ab; ist sie nicht MWST-pflichtig, wird sie es für diese Bezüge, sobald sie 10 000 CHF im
 * Kalenderjahr übersteigen (Art. 45 Abs. 2 lit. b MWSTG). Rein.
 */
import { countryName, normalizeCountry } from "./countries.js";
import type { Language } from "./types.js";

export interface AcquisitionTaxInput {
  agencyCountry: string | null;
  /** Land des Creators (Sitz bzw. Wohnsitz) */
  creatorCountry: string | null;
  /** Agentur ist nicht MWST-pflichtig (in CreatorOffice: Kleinunternehmer-Schalter) */
  agencyNotRegistered: boolean;
}

export function acquisitionTaxHint(input: AcquisitionTaxInput, lang: Language = "de"): string | null {
  const agency = normalizeCountry(input.agencyCountry);
  const creator = normalizeCountry(input.creatorCountry);
  if (!agency || !creator || (agency !== "CH" && agency !== "LI") || creator === "CH" || creator === "LI") return null;
  const land = countryName(creator, lang);
  // Liechtenstein hat ein eigenes MWSTG (gleiche Artikel wie das Schweizer), zuständig ist die Steuerverwaltung des Fürstentums
  const li = agency === "LI";
  if (lang === "en") {
    const act = li ? "Art. 45 Liechtenstein VAT Act" : "Art. 45 Swiss VAT Act";
    const vat = li ? "Liechtenstein VAT" : "Swiss VAT";
    const office = li ? "registration with the Liechtenstein Tax Administration" : "registration with the Swiss FTA";
    return `Service from abroad (${land}) to an agency in ${li ? "Liechtenstein" : "Switzerland"}: mind the acquisition tax (${act}). The invoice contains no ${vat}; `
      + (input.agencyNotRegistered
        ? `an agency not registered for VAT becomes liable once such services exceed CHF 10,000 in a calendar year (${office}).`
        : "the agency declares the tax on this service itself in its VAT return (and deducts it as input tax where entitled).")
      + " CreatorOffice does not calculate it.";
  }
  const act = li ? "Art. 45 MWSTG (FL)" : "Art. 45 MWSTG";
  const vat = li ? "liechtensteinische MWST" : "Schweizer MWST";
  const office = li ? "Anmeldung bei der Steuerverwaltung Liechtenstein" : "Anmeldung bei der ESTV";
  return `Leistung aus dem Ausland (${land}) an eine Agentur in ${li ? "Liechtenstein" : "der Schweiz"}: Bezugsteuer beachten (${act}). Die Rechnung enthält keine ${vat}; `
    + (input.agencyNotRegistered
      ? `eine nicht MWST-pflichtige Agentur wird für solche Bezüge steuerpflichtig, sobald sie 10 000 CHF im Kalenderjahr übersteigen (${office}).`
      : "die Agentur deklariert die Steuer auf diesem Bezug selbst in ihrer MWST-Abrechnung (und zieht sie, soweit berechtigt, als Vorsteuer ab).")
    + " CreatorOffice berechnet sie nicht.";
}
